import React, { useState, useEffect, useMemo } from 'react';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import axios from 'axios';
import PropTypes from "prop-types";
import { Table, Badge, Button, Modal, Accordion, Card, Pagination, Row, Col, Form, InputGroup } from 'react-bootstrap';
import AddTrips from './PopUps/AddTrips';
import EditTrips from './PopUps/EditTrips';
import CartaPorteButton from './PopUps/CartaPorteButton';
import FacturaButton from './PopUps/FacturaButton';
import * as Icon from 'react-feather';
import { canPageAction } from '../../config/roleConfig';
import DeactivateConfirmModal from '../Common/DeactivateConfirmModal';
import { MEXICO_CITY_TIME_ZONE } from '../../utils/dateTime';

function NewTrips(props)  {
    const authHeader = useAuthHeader();
    const authUser = useAuthUser();
    const currentUser = authUser();
    const canRead = canPageAction(currentUser, 'operations.trips', 'read');
    const canWrite = canPageAction(currentUser, 'operations.trips', 'write');
    const canDelete = canPageAction(currentUser, 'operations.trips', 'delete');

    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [expandedTrip, setExpandedTrip] = useState(null);
    const [tripToDeactivate, setTripToDeactivate] = useState(null);
    const [deactivatingTrip, setDeactivatingTrip] = useState(false);
    const apiUrl = process.env.REACT_APP_URL;

    // Filter states
    const [searchFilter, setSearchFilter] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [showFilters, setShowFilters] = useState(true);

    const get_all_trips = () => {
        axios({
            method:'GET',
            url:apiUrl+'/trips/get_all_trips',
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setData(response.data.msg.Trips);
        })
        .catch(error => {
            alert(error);
            console.log(error);
        });
    }

    useEffect(() => {
        get_all_trips();
    }, []);

    const toggleTripDetails = (tripId) => {
        setExpandedTrip(expandedTrip === tripId ? null : tripId);
    };

    const deleteTrip = (trip) => {
        if (!trip) {
            return;
        }

        setDeactivatingTrip(true);
        axios({
            method: 'DELETE',
            url: apiUrl + '/trips/delete',
            headers: {
                Authorization: authHeader()
            },
            data: { id: trip.id }
        }).then(() => {
            setTripToDeactivate(null);
            get_all_trips();
        }).catch(error => {
            alert(error.response?.data?.error || 'No se pudo desactivar el viaje');
            console.log(error);
        }).finally(() => {
            setDeactivatingTrip(false);
        });
    };

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'entregado':
                return <Badge bg="success">Entregado</Badge>;
            case 'pendiente':
                return <Badge bg="warning">Pendiente</Badge>;
            case 'cancelado':
                return <Badge bg="danger">Cancelado</Badge>;
            case 'en camino':
                return <Badge bg="info">En Camino</Badge>;
            default:
                return <Badge bg="secondary">{status || 'N/A'}</Badge>;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-MX', {
            timeZone: MEXICO_CITY_TIME_ZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('es-MX', {
            timeZone: MEXICO_CITY_TIME_ZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        return timeString;
    };

    const refreshData = () => {
        get_all_trips();
    };

    // Filter logic using useMemo for performance
    const filteredData = useMemo(() => {
        return data.filter(trip => {
            // Unified search filter - search across factura, cliente, operador, carga, and unidad
            if (searchFilter) {
                const searchLower = searchFilter.toLowerCase();
                const matchesFactura = trip.invoice_id?.toLowerCase().includes(searchLower);
                const matchesCliente = trip.company?.toLowerCase().includes(searchLower);
                const matchesOperador = trip.operator?.toLowerCase().includes(searchLower);
                const matchesCarga = trip.details?.toLowerCase().includes(searchLower);
                const matchesUnidad = trip.inventory?.toLowerCase().includes(searchLower);

                if (!matchesFactura && !matchesCliente && !matchesOperador && !matchesCarga && !matchesUnidad) {
                    return false;
                }
            }

            // Filter by date range
            if (filterStartDate || filterEndDate) {
                const tripStartDate = trip.start_date ? new Date(trip.start_date) : null;

                if (filterStartDate && tripStartDate) {
                    const filterStart = new Date(filterStartDate);
                    if (tripStartDate < filterStart) {
                        return false;
                    }
                }

                if (filterEndDate && tripStartDate) {
                    const filterEnd = new Date(filterEndDate);
                    filterEnd.setHours(23, 59, 59, 999); // Include the entire end date
                    if (tripStartDate > filterEnd) {
                        return false;
                    }
                }
            }

            return true;
        });
    }, [data, searchFilter, filterStartDate, filterEndDate]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchFilter, filterStartDate, filterEndDate]);

    const clearFilters = () => {
        setSearchFilter('');
        setFilterStartDate('');
        setFilterEndDate('');
    };

    const hasActiveFilters = searchFilter || filterStartDate || filterEndDate;

    // Pagination logic - use filteredData instead of data
    const indexOfLastTrip = currentPage * itemsPerPage;
    const indexOfFirstTrip = indexOfLastTrip - itemsPerPage;
    const currentTrips = filteredData.slice(indexOfFirstTrip, indexOfLastTrip);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
        <>
        <div className="card mb-4">
            <div className="card-body">
                <div className="card-header d-flex justify-content-between">
                    <h5 className="card-title">Registros de Viajes</h5>
                    <div className="d-flex gap-2">
                        <Button
                            variant={showFilters ? "primary" : "outline-primary"}
                            size="sm"
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <Icon.Filter size={14} className="me-1" />
                            Filtros {hasActiveFilters && <Badge bg="danger" className="ms-1">{[searchFilter, filterStartDate, filterEndDate].filter(Boolean).length}</Badge>}
                        </Button>
                        {canWrite && <AddTrips setData={refreshData}/>}
                    </div>
                </div>

                {/* Filter Section */}
                {showFilters && (
                    <div className="bg-light p-3 rounded mb-3">
                        <Row className="g-2 align-items-end">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small mb-1">Buscar (Factura, Cliente, Operador, Unidad, Carga)</Form.Label>
                                    <InputGroup size="sm">
                                        <InputGroup.Text>
                                            <Icon.Search size={14} />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="Buscar por factura, cliente, operador, unidad o carga..."
                                            value={searchFilter}
                                            onChange={(e) => setSearchFilter(e.target.value)}
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small mb-1">Fecha Desde</Form.Label>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        value={filterStartDate}
                                        onChange={(e) => setFilterStartDate(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small mb-1">Fecha Hasta</Form.Label>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        value={filterEndDate}
                                        onChange={(e) => setFilterEndDate(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                    Mostrando {filteredData.length} de {data.length} viajes
                                </small>
                                {hasActiveFilters && (
                                    <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                                        <Icon.X size={14} className="me-1" />
                                        Limpiar Filtros
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </div>
                )}

                <div>
                    <Table className="m-0" responsive>
                        <thead>
                            <tr>
                                <th>Factura</th>
                                <th>Cliente</th>
                                <th>Operador</th>
                                <th>Unidad</th>
                                <th>Fecha Inicio</th>
                                <th>Fecha Fin</th>
                                <th>Estado General</th>
                                <th>Carga</th>
                                <th>No. Paradas</th>
                                <th>Carta Porte</th>
                                <th>Factura</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentTrips.map(trip => (
                                <React.Fragment key={trip.id}>
                                    <tr>
                                        <td>{trip.invoice_id}</td>
                                        <td>{trip.company}</td>
                                        <td>{trip.operator}</td>
                                        <td>{trip.inventory}</td>
                                        <td>{trip.start_date}</td>
                                        <td>{trip.end_date}</td>
                                        <td>{getStatusBadge(trip.status)}</td>
                                        <td>{trip.details}</td>
                                        <td>
                                            <Badge bg="info">{trip.stops?.length || 0}</Badge>
                                        </td>
                                        <td>
                                            <CartaPorteButton
                                                tripId={trip.id}
                                                cartaPorteStatus={trip.carta_porte_status}
                                                setData={refreshData}
                                            />
                                        </td>
                                        <td>
                                            <FacturaButton
                                                tripId={trip.id}
                                                setData={refreshData}
                                            />
                                        </td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                {canRead && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => toggleTripDetails(trip.id)}
                                                    >
                                                        <Icon.Eye size={14} />
                                                    </Button>
                                                )}
                                                {canWrite && (
                                                    <EditTrips
                                                        setData={refreshData}
                                                        tripInfo={trip}
                                                    />
                                                )}
                                                {canDelete && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => setTripToDeactivate(trip)}
                                                    >
                                                        <Icon.Trash2 size={14} />
                                                    </Button>
                                                )}
                                                {!canRead && !canWrite && !canDelete && (
                                                    <span className="text-muted">N/A</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedTrip === trip.id && (
                                        <tr>
                                            <td colSpan="12">
                                                <div className="p-3 bg-light">
                                                    <h6>Detalles del Viaje</h6>
                                                    <div className="row mb-3">
                                                        <div className="col-md-4">
                                                            <p><strong>ID del Viaje:</strong> {trip.id}</p>
                                                            <p><strong>No. Factura:</strong> {trip.invoice_id || 'N/A'}</p>
                                                            <p><strong>CFDI Traslado:</strong> {trip.cfditraslado || 'N/A'}</p>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <p><strong>Operaciones:</strong> {trip.operations || 'N/A'}</p>
                                                            <p><strong>Monitor:</strong> {trip.monitor || 'N/A'}</p>
                                                            <p><strong>Estado de Pago:</strong> {trip.is_paid ? 'Pagado' : 'Pendiente'}</p>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <p><strong>Fecha de Inicio:</strong> {trip.start_date}</p>
                                                            <p><strong>Fecha de Fin:</strong> {trip.end_date}</p>
                                                            <p><strong>Última Actualización:</strong> {trip.updated}</p>
                                                        </div>
                                                    </div>

                                                    <h6>Paradas del Viaje</h6>
                                                    <Accordion>
                                                        {trip.stops?.map((stop, stopIndex) => (
                                                            <Accordion.Item
                                                                eventKey={stopIndex.toString()}
                                                                key={`${trip.id}-stop-${stopIndex}`}
                                                            >
                                                                <Accordion.Header>
                                                                    <div className="d-flex justify-content-between w-100 me-3">
                                                                        <span>Parada {stopIndex + 1}: {stop.origin_address} → {stop.destination_address}</span>
                                                                        {getStatusBadge(stop.status)}
                                                                    </div>
                                                                </Accordion.Header>
                                                                <Accordion.Body>
                                                                    {/* Trip Route Card */}
                                                                    <div className="mb-4">
                                                                        <Card className="border-0 bg-light">
                                                                            <Card.Body className="py-3">
                                                                                <Row className="align-items-center">
                                                                                    <Col md={4} className="text-center">
                                                                                        <div className="mb-2">
                                                                                            <h6 className="mb-1 text-primary">Origen</h6>
                                                                                            <div className="fw-bold fs-5">{formatDate(stop.origin_date)}</div>
                                                                                            <div className="text-muted small">{formatTime(stop.origin_time)}</div>
                                                                                        </div>
                                                                                        <div className="text-center border-0 bg-transparent fw-semibold" style={{ fontSize: '0.9rem' }}>
                                                                                            {stop.origin_address}
                                                                                        </div>
                                                                                    </Col>

                                                                                    <Col md={4} className="text-center">
                                                                                        <div className="d-flex align-items-center justify-content-center">
                                                                                            <div className="border-top border-2 border-primary flex-grow-1"></div>
                                                                                            <div className="mx-3">
                                                                                                <Icon.Truck size={24} className="text-primary" />
                                                                                            </div>
                                                                                            <div className="border-top border-2 border-primary flex-grow-1"></div>
                                                                                        </div>
                                                                                        <div className="mt-2">
                                                                                            <Badge bg={stop.status === 'Entregado' ? 'success' : stop.status === 'En Camino' ? 'info' : stop.status === 'Cancelado' ? 'danger' : 'warning'}>
                                                                                                {stop.status || 'Pendiente'}
                                                                                            </Badge>
                                                                                        </div>
                                                                                    </Col>

                                                                                    <Col md={4} className="text-center">
                                                                                        <div className="mb-2">
                                                                                            <h6 className="mb-1 text-success">Destino</h6>
                                                                                            <div className="fw-bold fs-5">{formatDate(stop.destination_date)}</div>
                                                                                            <div className="text-muted small">{formatTime(stop.destination_time)}</div>
                                                                                        </div>
                                                                                        <div className="text-center border-0 bg-transparent fw-semibold" style={{ fontSize: '0.9rem' }}>
                                                                                            {stop.destination_address}
                                                                                        </div>
                                                                                    </Col>
                                                                                </Row>
                                                                            </Card.Body>
                                                                        </Card>
                                                                    </div>

                                                                    {/* Additional Details */}
                                                                    <Row className="mb-3">
                                                                        <Col md={3}>
                                                                            <div className="text-center">
                                                                                <Icon.Package size={20} className="text-muted mb-1" />
                                                                                <h6 className="small mb-1">Tipo de Carga</h6>
                                                                                <div className="fw-semibold">{stop.load_type || 'N/A'}</div>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={3}>
                                                                            <div className="text-center">
                                                                                <Icon.Layers size={20} className="text-muted mb-1" />
                                                                                <h6 className="small mb-1">Peso</h6>
                                                                                <div className="fw-semibold">{stop.weight ? `${stop.weight} ton` : 'N/A'}</div>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={3}>
                                                                            <div className="text-center">
                                                                                <Icon.Clock size={20} className="text-muted mb-1" />
                                                                                <h6 className="small mb-1">Estado Actual</h6>
                                                                                <div>{getStatusBadge(stop.status)}</div>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={3}>
                                                                            <div className="text-center">
                                                                                <Icon.BarChart size={20} className="text-muted mb-1" />
                                                                                <h6 className="small mb-1">Progreso</h6>
                                                                                <div className="fw-semibold">
                                                                                    {stop.status === 'Entregado' ? '100%' :
                                                                                     stop.status === 'En Camino' ? '50%' :
                                                                                     stop.status === 'Cancelado' ? '0%' : '25%'}
                                                                                </div>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </Accordion.Body>
                                                            </Accordion.Item>
                                                        ))}
                                                    </Accordion>

                                                    {/* Maniobras Section */}
                                                    {trip.maneuver_records && trip.maneuver_records.length > 0 && (
                                                        <>
                                                            <h6 className="mt-4">Maniobras</h6>
                                                            <Accordion>
                                                                {trip.maneuver_records.map((maneuver, maneuverIndex) => (
                                                                    <Accordion.Item
                                                                        eventKey={`maneuver-${maneuverIndex}`}
                                                                        key={`${trip.id}-maneuver-${maneuverIndex}`}
                                                                    >
                                                                        <Accordion.Header>
                                                                            <div className="d-flex justify-content-between w-100 me-3">
                                                                                <span>
                                                                                    <Icon.Tool size={16} className="me-2" />
                                                                                    Maniobra {maneuverIndex + 1}: {maneuver.invoice_id || 'Sin Factura'}
                                                                                </span>
                                                                                <Badge bg="primary">${parseFloat(maneuver.total || 0).toFixed(2)}</Badge>
                                                                            </div>
                                                                        </Accordion.Header>
                                                                        <Accordion.Body>
                                                                            <Card className="border-0 bg-white">
                                                                                <Card.Body className="py-3">
                                                                                    <Row className="mb-3">
                                                                                        <Col md={6}>
                                                                                            <div className="mb-3">
                                                                                                <h6 className="mb-2 text-primary">Información de Facturación</h6>
                                                                                                <p className="mb-1"><strong>Factura:</strong> {maneuver.invoice_id || 'N/A'}</p>
                                                                                                <p className="mb-1"><strong>Subtotal:</strong> ${parseFloat(maneuver.subtotal || 0).toFixed(2)}</p>
                                                                                                <p className="mb-1"><strong>IVA (16%):</strong> ${parseFloat(maneuver.iva || 0).toFixed(2)}</p>
                                                                                                <p className="mb-0"><strong className="text-success">Total:</strong> <strong className="text-success fs-5">${parseFloat(maneuver.total || 0).toFixed(2)}</strong></p>
                                                                                            </div>
                                                                                        </Col>
                                                                                        <Col md={6}>
                                                                                            <div className="mb-3">
                                                                                                <h6 className="mb-2 text-info">Información del Ejecutor</h6>
                                                                                                <p className="mb-1">
                                                                                                    <strong>Realizado por:</strong>
                                                                                                    {maneuver.performed_by_trip_operator ? (
                                                                                                        <Badge bg="success" className="ms-2">Operador del Viaje</Badge>
                                                                                                    ) : (
                                                                                                        <span className="ms-2">{maneuver.performer_name || 'Otro'}</span>
                                                                                                    )}
                                                                                                </p>
                                                                                                {!maneuver.performed_by_trip_operator && (
                                                                                                    <p className="mb-1">
                                                                                                        <strong>Monto Pagado:</strong>
                                                                                                        <span className="ms-2">${parseFloat(maneuver.amount_paid || 0).toFixed(2)}</span>
                                                                                                    </p>
                                                                                                )}
                                                                                                {maneuver.description && (
                                                                                                    <p className="mb-0">
                                                                                                        <strong>Descripción:</strong>
                                                                                                        <span className="ms-2">{maneuver.description}</span>
                                                                                                    </p>
                                                                                                )}
                                                                                            </div>
                                                                                        </Col>
                                                                                    </Row>

                                                                                    {/* Financial Summary */}
                                                                                    <Row>
                                                                                        <Col md={3}>
                                                                                            <div className="text-center">
                                                                                                <Icon.DollarSign size={20} className="text-muted mb-1" />
                                                                                                <h6 className="small mb-1">Subtotal</h6>
                                                                                                <div className="fw-semibold">${parseFloat(maneuver.subtotal || 0).toFixed(2)}</div>
                                                                                            </div>
                                                                                        </Col>
                                                                                        <Col md={3}>
                                                                                            <div className="text-center">
                                                                                                <Icon.Percent size={20} className="text-muted mb-1" />
                                                                                                <h6 className="small mb-1">IVA (16%)</h6>
                                                                                                <div className="fw-semibold">${parseFloat(maneuver.iva || 0).toFixed(2)}</div>
                                                                                            </div>
                                                                                        </Col>
                                                                                        <Col md={3}>
                                                                                            <div className="text-center">
                                                                                                <Icon.CreditCard size={20} className="text-muted mb-1" />
                                                                                                <h6 className="small mb-1">Total</h6>
                                                                                                <div className="fw-semibold text-success">${parseFloat(maneuver.total || 0).toFixed(2)}</div>
                                                                                            </div>
                                                                                        </Col>
                                                                                        <Col md={3}>
                                                                                            <div className="text-center">
                                                                                                <Icon.User size={20} className="text-muted mb-1" />
                                                                                                <h6 className="small mb-1">Pagado a Ejecutor</h6>
                                                                                                <div className="fw-semibold">
                                                                                                    {maneuver.performed_by_trip_operator ? 'N/A' : `$${parseFloat(maneuver.amount_paid || 0).toFixed(2)}`}
                                                                                                </div>
                                                                                            </div>
                                                                                        </Col>
                                                                                    </Row>
                                                                                </Card.Body>
                                                                            </Card>
                                                                        </Accordion.Body>
                                                                    </Accordion.Item>
                                                                ))}
                                                            </Accordion>
                                                        </>
                                                    )}
                                                    {trip.stay_records && trip.stay_records.length > 0 && (
                                                        <>
                                                            <h6 className="mt-4">Estadias</h6>
                                                            <Accordion>
                                                                {trip.stay_records.map((stay, stayIndex) => (
                                                                    <Accordion.Item
                                                                        eventKey={`stay-${stayIndex}`}
                                                                        key={`${trip.id}-stay-${stay.id || stayIndex}`}
                                                                    >
                                                                        <Accordion.Header>
                                                                            <div className="d-flex justify-content-between w-100 me-3">
                                                                                <span>
                                                                                    <Icon.Clock size={16} className="me-2" />
                                                                                    Estadia {stayIndex + 1}: {stay.invoice_id || 'Sin Factura'}
                                                                                </span>
                                                                                <Badge bg="info">${parseFloat(stay.total || 0).toFixed(2)}</Badge>
                                                                            </div>
                                                                        </Accordion.Header>
                                                                        <Accordion.Body>
                                                                            <Card className="border-0 bg-white">
                                                                                <Card.Body className="py-3">
                                                                                    <Row className="mb-3">
                                                                                        <Col md={6}>
                                                                                            <h6 className="mb-2 text-primary">Información de Facturación</h6>
                                                                                            <p className="mb-1"><strong>Factura:</strong> {stay.invoice_id || 'N/A'}</p>
                                                                                            <p className="mb-1"><strong>Número de días:</strong> {parseInt(stay.numero_dias || 0, 10)}</p>
                                                                                            <p className="mb-1"><strong>Valor unitario:</strong> ${parseFloat(stay.valor_unitario || 0).toFixed(2)}</p>
                                                                                            <p className="mb-1"><strong>Importe:</strong> ${parseFloat(stay.subtotal || 0).toFixed(2)}</p>
                                                                                            <p className="mb-1"><strong>IVA (16%):</strong> ${parseFloat(stay.iva || 0).toFixed(2)}</p>
                                                                                            <p className="mb-0"><strong className="text-success">Total:</strong> <strong className="text-success fs-5">${parseFloat(stay.total || 0).toFixed(2)}</strong></p>
                                                                                        </Col>
                                                                                        <Col md={6}>
                                                                                            <h6 className="mb-2 text-info">Observaciones</h6>
                                                                                            <p>{stay.description || 'N/A'}</p>
                                                                                            <FacturaButton
                                                                                                tripId={trip.id}
                                                                                                stayId={stay.id}
                                                                                                defaultObservations={stay.description || ''}
                                                                                                hideObservationsInput
                                                                                                setData={refreshData}
                                                                                            />
                                                                                        </Col>
                                                                                    </Row>
                                                                                </Card.Body>
                                                                            </Card>
                                                                        </Accordion.Body>
                                                                    </Accordion.Item>
                                                                ))}
                                                            </Accordion>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            {currentTrips.length === 0 && (
                                <tr>
                                    <td colSpan="9" className="text-center text-muted">
                                        No hay viajes registrados
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                            <Pagination>
                                <Pagination.First
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                />

                                {Array.from({ length: totalPages }, (_, index) => {
                                    const page = index + 1;
                                    if (
                                        page === 1 ||
                                        page === totalPages ||
                                        (page >= currentPage - 2 && page <= currentPage + 2)
                                    ) {
                                        return (
                                            <Pagination.Item
                                                key={page}
                                                active={page === currentPage}
                                                onClick={() => setCurrentPage(page)}
                                            >
                                                {page}
                                            </Pagination.Item>
                                        );
                                    } else if (
                                        page === currentPage - 3 ||
                                        page === currentPage + 3
                                    ) {
                                        return <Pagination.Ellipsis key={page} />;
                                    }
                                    return null;
                                })}

                                <Pagination.Next
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}

                    <div className="mt-2 text-center text-muted">
                        Mostrando {indexOfFirstTrip + 1} a {Math.min(indexOfLastTrip, filteredData.length)} de {filteredData.length} viajes
                        {hasActiveFilters && <span> (filtrado de {data.length} total)</span>}
                    </div>
                </div>
            </div>
        </div>
        <DeactivateConfirmModal
            show={Boolean(tripToDeactivate)}
            title="Desactivar Viaje"
            itemLabel={tripToDeactivate?.invoice_id || `Viaje ${tripToDeactivate?.id}`}
            body="Este viaje se desactivará y dejará de aparecer en los registros principales. La información relacionada se conservará."
            confirming={deactivatingTrip}
            onCancel={() => !deactivatingTrip && setTripToDeactivate(null)}
            onConfirm={() => deleteTrip(tripToDeactivate)}
        />
        </>
    );

}

//Status: Entregado, Pendiente, Cancelado, En camino
// Success, Warning, Danger, Info
//https://react-bootstrap.netlify.app/docs/components/badge/
export default NewTrips;
